<template>
    <div class="lg:hidden bg-zinc-900 px-4 pt-4 pb-3 border-b border-white/10">
        <!-- View: All -->
        <template v-if="view === 'all' || view === 'explore'">
            <h1 class="text-2xl font-bold text-white tracking-tight">
                Sponsor Auditions. Build Your Legacy.
            </h1>

            <!-- Guest Content -->
            <template v-if="!isUserLoggedIn">
                <p class="mt-1.5 text-sm text-white/60 leading-relaxed">
                    Give stars to build your Sponsor profile — earn badges, climb the leaderboard, and get credit for scenes you help create.
                </p>

                <div class="mt-3">
                    <button
                        @click="$emit('joinClick')"
                        class="w-full h-10 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-sm font-bold transition-all shadow-lg shadow-blue-600/25"
                    >
                        Get Stars to Sponsor
                    </button>
                </div>
            </template>

            <!-- Logged In Content -->
            <template v-else>
                <p class="mt-1.5 text-sm text-white/60 leading-relaxed">
                    Give stars to build your Sponsor profile and show off the performers you've backed.
                </p>
            </template>

            <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-white/50">
                <div class="flex items-center gap-1.5">
                    <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span>
                        <span class="text-white/90 font-semibold">
                          {{ formatStars(totalStarsGiven) }}
                        </span>
                        stars given
                    </span>
                </div>

                <div class="h-3 w-px bg-white/10" />

                <div class="flex items-center gap-1.5">
                    <TrendingUp class="w-3.5 h-3.5 text-white/40" />
                    <span>
                        <span class="text-white/90 font-semibold">{{ sponsoredLast30Days }}</span> sponsored last 30 days
                    </span>
                </div>

                <div class="flex items-center">
                    <button
                        type="button"
                        @click="$emit('myNaClicked')"
                        class="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                    >
                        {{ isUserLoggedIn ? 'View Your Sponsor Page' : 'Create Your Sponsor Page'}}
                        <ExternalLink class="w-4 h-4" />
                    </button>
                </div>
            </div>

            <!-- Top Sponsors - Inline on Mobile -->
            <div class="mt-3 pt-2.5 border-t border-white/[0.06]">
                <div class="overflow-x-auto" style="scrollbar-width: none;">
                    <div class="flex items-center gap-2">
                        <button class="flex-shrink-0 flex items-center gap-1.5 text-white/60 text-xs font-bold">
                            <Trophy class="w-3.5 h-3.5 text-amber-400" />
                            Top:
                        </button>
                        <button
                            v-for="(sponsor, index) in topSponsors.slice(0, maxVisibleTopSponsors)"
                            :key="sponsor.user_id"
                            @click="goToSponsorProfile(sponsor)"
                            class="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full border active:bg-white/[0.12] transition" :class="rankBorderColor(sponsor.rank)"
                        >
                            <span class="w-4 h-4 rounded-full bg-gradient-to-br flex items-center justify-center text-[8px] font-bold shrink-0" :class="rankBadgeColor(sponsor.rank)">{{ sponsor.rank }}</span>
                            <span class="text-white text-[11px] font-semibold whitespace-nowrap">{{ sponsor.sponsor_name }}</span>
                            <span class="text-white/50 text-[11px] font-semibold">{{ sponsor.total_stars }}</span>
                            <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-2.5 h-2.5" />
                        </button>

                        <!-- View All pill at end -->
                        <button
                            @click="$emit('openLeaderboard')"
                            class="flex-shrink-0 text-white/40 text-[11px] font-semibold whitespace-nowrap px-2"
                        >
                            All →
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <!-- View: Mine -->
        <template v-if="view === 'mine'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                My Sponsorships
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Track what you've sponsored and see which auditions are closest to
                being released.
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <span>
                    <span class="text-white/90 font-semibold">
                        {{ userSponsoredCount }}
                    </span>
                    auditions sponsored
                </span>
                <div class="h-3 w-px bg-white/10" />
                <span>
                    <span class="text-white/90 font-semibold">
                        {{ userTotalStarsGiven }}
                    </span>
                    stars given
                </span>
            </div>
        </template>

        <!-- View: Sponsored -->
        <template v-if="view === 'sponsored'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                Community-Backed Auditions
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Auditions that have received star support from members, working their way toward a full production.
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <span>
                    <span class="text-white/90 font-semibold">{{ totalSponsored }}</span>
                    auditions sponsored
                </span>
                <div class="h-3 w-px bg-white/10" />
                <div class="flex items-center gap-1.5">
                    <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span>
                        <span class="text-white/90 font-semibold">{{ formatStars(sponsoredTotalStarsGiven) }}</span> stars given
                    </span>
                </div>
            </div>
        </template>

        <!-- View: Released -->
        <template v-if="view === 'released'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                Sponsored & Released
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Watch the released scenes made possible by community sponsorship.
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <span>
                    <span class="text-white/90 font-semibold">
                        {{ releasedCount }}
                    </span>
                    scenes released
                </span>
                <div class="h-3 w-px bg-white/10" />
                <span>
                    <span class="text-white/90 font-semibold">{{ sponsoredLast30Days }}</span> sponsored last 30 days
                </span>
            </div>
        </template>

        <!-- View: Castings -->
        <template v-if="view === 'castings'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                Pitch Requests
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Fan-requested scenes waiting to happen. Support a pitch and help bring it to life.
            </p>

            <Link
                :href="getCreateCastingUrl(isUserLoggedIn, routePrefix)"
                @click="handleCreateCastingClick"
                class="mt-4 flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors"
            >
                + Submit a Pitch Request
            </Link>

            <button
                type="button"
                @click="handleCastingsPageClick"
                class="mt-2 flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors"
            >
                Your Pitches
                <ExternalLink class="w-4 h-4" />
            </button>
        </template>
    </div>
</template>

<script setup>
import {ExternalLink, TrendingUp, Trophy} from 'lucide-vue-next'
import {ref} from "vue";
import {Link, router} from '@inertiajs/vue3'
import {getFathomPagePrefix, trackEvent, trackFathomEvent} from "@/Helpers/analytics";
import {getCreateCastingUrl} from "@/Helpers/auditions";

const props = defineProps({
    view: {
        type: String,
        required: true,
        validator: (value) => ['all', 'explore', 'mine', 'released', 'sponsored', 'castings'].includes(value)
    },
    isUserLoggedIn: {
        type: Boolean,
        required: true
    },
    sponsoredThisMonth: {
        type: Number,
        required: true
    },
    sponsoredLast30Days: {
        type: Number,
        required: true
    },
    totalSponsored: {
        type: Number,
        required: true
    },
    sponsoredTotalStarsGiven: {
        type: Number,
        required: true
    },
    totalStarsGiven: {
        type: Number,
        required: true
    },
    userSponsoredCount: {
        type: Number,
        required: true
    },
    releasedCount: {
        type: Number,
        required: true
    },
    userTotalStarsGiven: {
        type: Number,
        required: true
    },
    topSponsors: {
        type: Array,
        default: []
    },
    routePrefix: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['openLeaderboard', 'joinClick', 'myNaClicked', 'castingsPageClick'])

const maxVisibleTopSponsors = ref(10)

const formatStars = (num) => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M+`;
    }
    if (num >= 100000) {
        return `${Math.floor(num / 1000)}K+`;
    }
    return num.toLocaleString()
}

const rankBorderColor = rank => {
    if (rank === 1) return 'bg-amber-500/[0.08] border-amber-400/30'
    if (rank === 2) return 'bg-zinc-400/[0.06] border-zinc-400/20'
    if (rank === 3) return 'bg-amber-700/[0.06] border-amber-700/25'
    return 'bg-white/[0.06] border-white/10'
}

const rankBadgeColor = rank => {
    if (rank === 1) return 'from-amber-300 to-amber-500 text-black'
    if (rank === 2) return 'from-zinc-300 to-zinc-500 text-black'
    if (rank === 3) return 'from-amber-600 to-amber-800 text-amber-100'
    return 'bg-white/20 text-white/80'
}

const goToSponsorProfile = (sponsor) => {
    trackEvent('MyNA Clicked')
    trackFathomEvent('MyNA Clicked')

    router.visit(route(props.routePrefix + '.profile.my', { slug: sponsor.sponsor_name }), {
        preserveScroll: false,
        preserveState: true,
    })
}

const handleCreateCastingClick = () => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Hero_Create_Casting_Button_Clicked')
}

const handleCastingsPageClick = () => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Hero_Castings_Page_Clicked')
    emit('castingsPageClick')
}
</script>
